import React, { useState, useEffect } from 'react';
import '../view-archer-styles.css';

// material-ui
import { Button, Chip, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, FormGroup, FormControlLabel, FormControl, MenuItem, Input, InputLabel, Paper, Select, Stack, TextField, Typography} from '@mui/material/';
import { blue} from '@mui/material/colors';

//components import
import AlertInfoDialog from '../../../components/elements/AlertInfoDialog';
import AlertInfo from '../../../dataObjects/AlertInfo';
import ArcherIssueListing from '../dataObjects/ArcherIssueListing';

interface NewReferralProps {
    isNewReferralDialogOpen: boolean,
    closeNewReferralDialog: () => void
}

interface FilerInfo {
    label: string;
    value: number;
    districtCodeSought: number;
}

const NewReferralDialog = (props: NewReferralProps) => {

    //initial state set up
    const [searchInput, setSearchInput] = useState('');
    const [filerListing, setFilerListing] = useState<FilerInfo[]>();
    const [issueListing, setIssueListing] = useState<ArcherIssueListing[]>();
    const [filerCpfId, setCpfId] = useState(0);

    //initial payload
    useEffect(() => {
        if (searchInput) {
            getFilerListing();
        }
    }, [searchInput]);


    const getFilerListing = () => {
        let url = `/Reference/GetFilerListings?searchPhrase=${searchInput}`;
        fetch(url)
            .then(result => result.json())
            .then(result => {
                setFilerListing(result);
            })
    }

    const updateSearchInput = (event: any) => {
        setTimeout(() => {
            setSearchInput(event.target.value);
        }, 1000)
    }

    const displayIssueStatusIcon = (status: string) => {
        let shortStatus = status;
        if (status.indexOf(",") > 0) {
            shortStatus = status.substring(0, status.indexOf(","));
        }

        switch (shortStatus) {
            case 'Delinquent':
                return (
                    <Chip variant="outlined" label="Delinquent" size="small" color="error" />
                );
            case 'Outstanding':
                return (
                    <Chip variant="outlined" label="Outstanding" size="small" color="error" />
                );
            case 'Held':
                return (
                    <Chip variant="outlined" label={status} size="small" color="primary" />
                );
            case 'Referred':
                return (
                    <Chip variant="outlined" label={status} size="small" color="success" />
                );
            default:
                return '';
        }
    }

    const getFilerPendingIssues = (event: any) => {
        let cpfId = event.target.value;
        setCpfId(cpfId);
        let url = `/Archer3/GetFilerIssueListings?cpfId=${cpfId}`
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: GetFilerIssueListings',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    let data = result.result;
                    setIssueListing(data);
                }
            }
        )
    }

    const [selectedIssueIds, setSelectedIssueIds] = useState<number[]>([]);

    const handleIssueCheckbox = (event: any)=> {
        let issueId = event.target.value;
        if (event.target.checked) {
            addIssueToIssueList(issueId);
        }
        else {
            removeIssueFromIssueList(issueId);
        }
    }

    const addIssueToIssueList = (issueId: number) => {
        if (issueId > 0) {
            setSelectedIssueIds([...selectedIssueIds, issueId]);
        }
    }

    const removeIssueFromIssueList = (issueId: number) => {
        if (issueId > 0) {
            setSelectedIssueIds(selectedIssueIds.filter(id => id !== issueId));
        }
    }

    const addNewReferral = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);
        const formJson = Object.fromEntries((formData as any).entries());
        const issuesDescription = formJson.issuesDescription;
        const relevantBackground = formJson.relevantBackground;
        const auditorRecommendation = formJson.auditorRecommendation;
        const phoneCallDate = formJson.phoneCallDate;
        const selectedIssueList = selectedIssueIds.toString(); 

        var params = [
            `cpfId=${filerCpfId}`,
            `issuesDescription=${issuesDescription}`,
            `relevantBackground=${relevantBackground}`,
            `auditorRecommendation=${auditorRecommendation}`,
            `phoneCallDate=${phoneCallDate}`,
            `IssueIds=${selectedIssueList}`
        ].join('&');

        //maybe update to use post
        let url = `/Archer3/SaveLegalReferral?${params}`
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: SaveLegalReferral',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    props.closeNewReferralDialog();
                    setSelectedIssueIds([]);
                }
            })
    }


    /*Error Handling*/
    const [isAlertInfoDialogOpen, setAlertInfoDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState<AlertInfo>();
    const closeAlertInfoDialog = () => {
        setAlertInfoDialog(false);
    }

    return (
        <>
            <Dialog
                open={props.isNewReferralDialogOpen}
                onClose={props.closeNewReferralDialog}
                PaperProps={{
                    component: 'form',
                    onSubmit: (event: React.FormEvent<HTMLFormElement>) => addNewReferral(event)
                }}
            >
                <DialogTitle>
                    <Typography sx={{ color: blue[800] }}>Open a Legal Referral</Typography>
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container>
                        <Stack direction="row" spacing={1}>
                            <TextField
                                size="small"
                                placeholder="Search Filer..."
                                onChange={(event) => updateSearchInput(event)}
                            />
                            <FormControl sx={{ m: 1, minWidth: 180 }} size="small">
                                <InputLabel>Select a filer</InputLabel>
                                <Select
                                    label="Select a filer"
                                    onChange={(event) => getFilerPendingIssues(event)}
                                >
                                    {
                                        filerListing &&
                                        filerListing.map((filer) =>
                                            <MenuItem key={filer.value} value={filer.value}>{filer.value} {filer.label}</MenuItem>
                                        )
                                    }
                                </Select>
                            </FormControl>
                        </Stack>
                    </Grid>
                    <Grid container sx={{ marginTop: 1, marginBottom: 1, maxHeight: 250, overflow: 'auto' }}>
                        <Grid item xs={12}>
                            <FormGroup>
                                {
                                    issueListing &&
                                    issueListing.map((issue: ArcherIssueListing) => 
                                        <FormControlLabel
                                            control={<Checkbox
                                                value={issue.issueId}
                                                onChange={handleIssueCheckbox}
                                            />}
                                            label={<>
                                                <span className={issue.status} >{issue.issueTypeCode} {issue.header} - {issue.date}</span> {displayIssueStatusIcon(issue.status)} 
                                            </>}
                                        />
                                        )
                                }
                            </FormGroup>
                        </Grid>
                    </Grid>
                    <Grid container>
                        <Grid item xs={12}>
                            <TextField
                                margin="dense"
                                name="issuesDescription"
                                label="Issue(s) Description"
                                multiline
                                fullWidth
                                rows={3}
                            />
                            <TextField
                                margin="dense"
                                name="relevantBackground"
                                label="Relevant Background"
                                multiline
                                fullWidth
                                rows={3}
                            />
                            <TextField
                                margin="dense"
                                name="auditorRecommendation"
                                label="Auditor Recommendation"
                                multiline
                                fullWidth
                                rows={3}
                            />
                            <TextField
                                margin="dense"
                                name="phoneCallDate"
                                label="Date Phone Call Made"
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={props.closeNewReferralDialog}>Cancel</Button>
                    <Button type="submit">Save</Button>
                </DialogActions>
            </Dialog>
            {
                alertInfo &&
                <AlertInfoDialog isAlertInfoDialogOpen={isAlertInfoDialogOpen} alertInfo={alertInfo} closeAlertInfoDialog={closeAlertInfoDialog}></AlertInfoDialog>
            }
        </>
    )
}

export default NewReferralDialog;